import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FcNext } from 'react-icons/fc';
import { useAuth } from '../../hooks/useAuth';
import type { NavItem } from '../../constants/sidebar';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

const findNavItemByPath = (
  items: NavItem[],
  targetPath: string,
  parentPath: BreadcrumbItem[] = []
): BreadcrumbItem[] | null => {
  for (const item of items) {
    const itemLabel = item.name ?? item.title;
    const itemPath = item.urlPath ?? item.path;
    const currentPath: BreadcrumbItem[] = [
      ...parentPath,
      { label: itemLabel, path: itemPath }
    ];

    if (itemPath === targetPath) {
      return currentPath;
    }

    if (item.children) {
      const found = findNavItemByPath(item.children, targetPath, currentPath);
      if (found) return found;
    }
  }
  return null;
};

export function Breadcrumb() {
  const location = useLocation();
  const { navigation } = useAuth();

  const breadcrumbItems = React.useMemo(() => {
    const path = location.pathname;
    
    // Special case for dashboard
    if (path === '/dashboard' || path === '/') {
      return [
        { label: 'Painel', path: '/' },
        { label: 'Dashboard', path: '/dashboard' }
      ];
    }

    // Special cases for status pages
    if (path === '/403') {
      return [{ label: 'Acesso Negado' }];
    }
    if (path === '/404' || path.startsWith('/404')) {
      return [{ label: 'Página Não Encontrada' }];
    }

    // Condominiums: force breadcrumb to Gestão de Condomínios (correct list path)
    if (path.startsWith('/condominios')) {
      const segments = path.split('/').filter(Boolean);
      const items: BreadcrumbItem[] = [
        { label: 'Gestão de Condomínios', path: '/gestao-condominios' },
      ];

      if (segments.length >= 3 && segments[2] === 'editar') {
        items.push({ label: 'Editar' });
      } else if (segments.length === 2) {
        items.push({ label: 'Detalhes' });
      } else if (segments.length >= 2 && segments[1] === 'novo') {
        items.push({ label: 'Novo' });
      }

      return items;
    }

    // Find the navigation item by path
    const navPath = findNavItemByPath(navigation || [], path);
    
    if (!navPath) {
      // If no exact match, try to find partial match for dynamic routes
      const pathSegments = path.split('/').filter(Boolean);
      let currentPath = '';
      
      for (let i = 0; i < pathSegments.length; i++) {
        currentPath += '/' + pathSegments[i];
        const found = findNavItemByPath(navigation || [], currentPath);
        if (found) {
          return found;
        }
      }
      
      // For unmatched paths, create a generic breadcrumb
      if (pathSegments.length > 0) {
        const fallbackItems: BreadcrumbItem[] = [];
        pathSegments.forEach((segment, index) => {
          const label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
          fallbackItems.push({
            label,
            path: index < pathSegments.length - 1 ? '/' + pathSegments.slice(0, index + 1).join('/') : undefined,
          });
        });

        return fallbackItems;
      }
      
      return [];
    }

    return navPath;
  }, [location.pathname, navigation]);

  if (breadcrumbItems.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className="mb-4 bg-white rounded-lg shadow-sm px-4 py-3">
      <ol className="flex items-center space-x-2 text-sm">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return (
            <li key={item.label} className="flex items-center">
              {index > 0 && (
                <FcNext className="h-4 w-4 mx-2" />
              )}
              {isLast || !item.path ? (
                <span className="text-blue-700 font-medium">
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className="text-blue-600 hover:text-blue-800 transition-colors hover:underline"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
